<template>
  <!-- HEADER -->
  <div class="header">
    <div class="container-fluid">
      <!-- Table -->
      <div class="card mt-5">
        <div class="card-header">
          <!-- Title -->
          <h4 class="card-header-title">Competency</h4>
          <!-- Action Buttons -->
          <div class="card-header-action">
            <b-button
              class="btn-pill me-2"
              variant="outline-info"
              @click="downloadTemplate()">
              <span class="fe fe-download"></span>
              Download Template
            </b-button>
            <b-button
              class="btn-pill me-2"
              variant="success"
              @click="showUploadModal()">
              <span class="fe fe-upload"></span>
              Upload File
            </b-button>
            <b-button
              class="btn-pill"
              variant="danger"
              @click="addData()">
              <span class="fe fe-file-plus"></span>
              Add Competency
            </b-button>
          </div>
        </div>
        <!-- Filter -->
        <div>
          <div class="card-body">
            <div class="row">
              <div class="col-md-3">
                <div class="form-group">
                  <v-select
                    v-model="filters.object_type"
                    :options="[
                      { value: '', label: 'All Object Types' },
                      { value: 'Q', label: 'Q' },
                      { value: 'QK', label: 'QK' }
                    ]"
                    :reduce="option => option.value"
                    label="label"
                    placeholder="Select Object Type"
                    @input="onSearch">
                  </v-select>
                </div>
              </div>
              <div class="col-md-3 offset-4">
                <div class="form-group">
                  <input
                    type="text"
                    v-model="filters.search"
                    class="form-control"
                    placeholder="Search by name..."
                    @input="onSearch" />
                </div>
              </div>
              <div class="col-md-2">
                <button
                  type="button"
                  class="btn btn-outline-primary"
                  @click="clearFilters">
                  <span class="fe fe-x"></span> Clear Filter
                </button>
              </div>
            </div>
          </div>
        </div>
        <!-- Loading State -->
        <div v-if="isLoading" class="card-body text-center py-5">
          <div class="spinner-border text-primary" role="status">
            <span class="sr-only">Loading...</span>
          </div>
          <p class="mt-2">Loading competencies...</p>
        </div>

        <!-- Cards Grid -->
        <div v-else-if="competencyData.length > 0" class="card-body">
          <div class="row">
            <div 
              class="col-lg-3 col-md-6 mb-4" 
              v-for="competency in competencyData" 
              @click.prevent="viewData(competency)"
              :key="competency.id">
              <div class="card h-100 shadow-sm">
                <!-- Card Image/Thumbnail -->
                <div class="card-img-top bg-light d-flex align-items-center justify-content-center">
                  <img 
                    :src="`${imageBaseUrl}/attachments/${competency.thumbnail_url}`" 
                    :alt="competency.name"
                    class="card-img-top"
                    style="height: 160px; object-fit: cover;"
                    @error="handleImageError">
                </div>
                
                <!-- Card Body -->
                <div class="card-body d-flex flex-column">
                  <!-- Competency Type Badge -->
                  <div class="mb-2">
                    <span :class="getCompetencyTypeBadgeClass(competency.object_type)">
                      {{ competency.object_type || '' }}
                    </span>
                  </div>
                  
                  <!-- Competency Name -->
                  <h5 class="card-title font-weight-bold text-truncate" :title="competency.name">
                    {{ competency.name || '' }}
                  </h5>

                  <!-- Competency Description -->
                  <span 
                    class="card-text text-muted small mb-2 text-truncate" 
                    v-html="competency.description || 'No description available.'"  />
                  
                  <!-- Competency ID -->
                  <p class="card-text text-muted small mb-2">
                    <i class="fe fe-hash"></i> ID: {{ competency.id }}
                  </p>
                  
                  <!-- Parent ID if exists -->
                  <p v-if="competency.parent_id" class="card-text text-muted small mb-2">
                    <i class="fe fe-git-branch"></i> Parent: {{ competency.parent_id }}
                  </p>
                  
                  <!-- Role Competency Info -->
                  <p v-if="competency.role_competency && competency.role_competency.role_competency_id" class="card-text text-muted small mb-3">
                    <i class="fe fe-briefcase"></i> Role ID: {{ competency.role_competency.role_competency_id }}
                  </p>
                  
                  <!-- Card Footer with Actions -->
                  <!-- <div class="mt-auto">
                    <div class="d-flex justify-content-end gap-2">
                      <b-button
                        variant="info"
                        size="sm"
                        class="btn-pill"
                        @click="viewData(competency)">
                        <span class="fe fe-eye"></span>
                      </b-button>
                      <b-button
                        variant="warning"
                        size="sm"
                        class="btn-pill"
                        @click="updateData(competency)">
                        <span class="fe fe-edit"></span>
                      </b-button>
                      <b-button
                        variant="danger"
                        size="sm"
                        class="btn-pill"
                        @click="deleteData(competency.id)">
                        <span class="fe fe-trash"></span>
                      </b-button>
                    </div>
                  </div> -->
                </div>
                
                <!-- Card Footer -->
                <div class="card-footer bg-light border-top">
                  <div class="row justify-content-end">
                    <!-- <div class="col-6">
                      <div class="d-flex align-items-center text-muted small">
                        <i class="fe fe-file-text me-1"></i>
                        <span class="me-3">123</span>
                        <i class="fe fe-users me-1"></i>
                        <span>999</span>
                      </div>
                    </div> -->
                    <div class="col-6">
                      <div class="d-flex justify-content-end">
                        <div class="dropdown" @click.stop>
                          <button 
                            class="btn btn-sm btn-light dropdown-toggle" 
                            type="button" 
                            :id="'dropdown-' + competency.id"
                            data-bs-toggle="dropdown" 
                            aria-expanded="false">
                            <i class="fe fe-more-horizontal"></i>
                          </button>
                          <ul class="dropdown-menu dropdown-menu-end" :aria-labelledby="'dropdown-' + competency.id">
                            <li>
                              <a 
                                class="dropdown-item"
                                style="cursor: pointer;"
                                @click.prevent="viewData(competency)">
                                <i class="fe fe-eye me-2"></i>View
                              </a>
                            </li>
                            <li>
                              <a 
                                class="dropdown-item" 
                                style="cursor: pointer;"
                                @click.prevent="updateData(competency)">
                                <i class="fe fe-edit me-2"></i>Edit
                              </a>
                            </li>
                            <li><hr class="dropdown-divider"></li>
                            <li>
                              <a 
                                class="dropdown-item text-danger" 
                                style="cursor: pointer;"
                                @click.prevent="deleteData(competency.id)">
                                <i class="fe fe-trash me-2"></i>Delete
                              </a>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <div v-else class="card-body text-center py-5">
          <div class="mb-3">
            <i class="fe fe-inbox" style="font-size: 48px; color: #6c757d;"></i>
          </div>
          <h5 class="text-muted">No competencies found</h5>
          <p class="text-muted">Try adjusting your search criteria or add a new competency.</p>
        </div>

        <!-- Manual Pagination -->
        <div v-if="!isLoading && competencyData.length > 0" class="card-footer bg-white">
          <div class="row align-items-center">
            <div class="col-md-6">
              <div class="d-flex align-items-center">
                <label class="me-2 mb-0">Rows per page:</label>
                <select 
                  v-model="body.limit" 
                  @change="onPerPageChange"
                  class="form-select form-select-sm" 
                  style="width: auto;">
                  <option value="10">10</option>
                  <option value="25">25</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </select>
                <span class="ms-3 text-muted small">
                  Showing {{ ((body.page - 1) * body.limit) + 1 }} to {{ Math.min(body.page * body.limit, totalItems) }} of {{ totalItems }} entries
                </span>
              </div>
            </div>
            <div class="col-md-6">
              <nav aria-label="Competency pagination">
                <ul class="pagination pagination-sm justify-content-end mb-0">
                  <!-- Previous Button -->
                  <li class="page-item" :class="{ disabled: body.page <= 1 }">
                    <button 
                      class="page-link" 
                      @click="changePage(body.page - 1)"
                      :disabled="body.page <= 1">
                      <i class="fe fe-chevron-left"></i>
                    </button>
                  </li>
                  
                  <!-- Page Numbers -->
                  <li 
                    v-for="page in visiblePages" 
                    :key="page"
                    class="page-item"
                    :class="{ active: page === body.page }">
                    <button 
                      class="page-link" 
                      @click="changePage(page)">
                      {{ page }}
                    </button>
                  </li>
                  
                  <!-- Next Button -->
                  <li class="page-item" :class="{ disabled: body.page >= totalPages }">
                    <button 
                      class="page-link" 
                      @click="changePage(body.page + 1)"
                      :disabled="body.page >= totalPages">
                      <i class="fe fe-chevron-right"></i>
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          </div>
        </div>
      </div>

      <!-- Modal -->
      <b-modal 
        id="modal-competency" 
        centered 
        hide-footer 
        :title="modalTitle"
        size="xl">
        <!-- Add Form -->
        <form @submit.prevent="saveAdd()" v-if="isAdd === true">
          <div class="row">
            <div class="col-md-6 row">
              <div class="col-md-12">
                <div class="form-group">
                  <label>Competency ID <span class="text-danger">*</span></label>
                  <input
                    v-validate="'required'"
                    name="Competency ID"
                    type="text"
                    v-model="bodyAdd.competency_id"
                    class="form-control"
                    placeholder="Competency ID" />
                  <span
                    class="text-sm text-danger"
                    v-show="errors.has('Competency ID')"
                    >{{ errors.first("Competency ID") }}</span>
                </div>
              </div>
              <div class="col-md-12">
                <div class="form-group">
                  <label>Name <span class="text-danger">*</span></label>
                  <input
                    v-validate="'required'"
                    name="Name"
                    type="text"
                    v-model="bodyAdd.competency_name"
                    class="form-control"
                    placeholder="Competency Name" />
                  <span
                    class="text-sm text-danger"
                    v-show="errors.has('Name')"
                    >{{ errors.first("Name") }}</span>
                </div>
              </div>
              <div class="col-md-6">
                <div class="form-group">
                  <label>Object Type</label>
                  <v-select
                    name="Object Type"
                    v-validate="'required'"
                    v-model="bodyAdd.object_type"
                    :options="[
                      { value: 'Q', label: 'Q' },
                      { value: 'QK', label: 'QK' }
                    ]"
                    :reduce="option => option.value"
                    label="label"
                    placeholder="Select Object Type">
                  </v-select>
                  <span
                    class="text-sm text-danger"
                    v-show="errors.has('Object Type')"
                    >{{ errors.first("Object Type") }}</span>
                </div>
              </div>
              <div class="col-md-6">
                <div class="form-group">
                  <label>Parent ID</label>
                  <v-select
                    v-model="bodyAdd.parent_id"
                    :options="competencyOptions"
                    :reduce="option => option.id"
                    :loading="isLoadingCompetencyOptions"
                    label="name"
                    placeholder="Select Parent Competency">
                    <template #spinner="{ loading }">
                      <div v-if="loading" class="vs__spinner">Loading...</div>
                    </template>
                  </v-select>
                </div>
              </div>
            </div>
            <div class="col-md-6">
              <div class="form-group">
                <label>Image</label>
                <ImageCropper
                  v-model="bodyAdd.image"
                  :preview-url="bodyAdd.imagePreview"
                  :aspect-ratio="1.777"
                  :max-file-size="2 * 1024 * 1024"
                  @cropped="onImageCroppedAdd"
                  @removed="onImageRemovedAdd"
                  @error="onImageErrorAdd"
                  @crop-status="onCropStatusChangeAdd"
                />
              </div>
            </div>
            <div class="col-md-12">
              <div class="form-group">
                <label>Description</label>
                <vue-editor
                  v-model="bodyAdd.description"
                  :editor-toolbar="editorToolbar"
                  placeholder="Enter competency description">
                </vue-editor>
              </div>
            </div>
          </div>
          
          <!-- Dynamic Positions & Proficiency Levels -->
          <div class="row">
            <div class="d-flex justify-content-between align-items-center">
              <h3 class="mt-2">Position & Proficiency</h3>
              <div class="d-flex align-items-center">
                <div class="form-check me-3">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    id="uniformLevelAdd"
                    v-model="bodyAdd.uniformLevel"
                    @change="toggleUniformLevelAdd">
                  <label class="form-check-label" for="uniformLevelAdd">
                    Set all positions to level 3
                  </label>
                </div>
                <b-button variant="primary" @click="addPositionProficiencyAdd">
                  <span class="fe fe-file-plus"></span>
                  Add Position
                </b-button>
              </div>
            </div>
            <div class="col-12 mt-3 mb-3">
              <b-alert variant="warning" show class="mb-0">
                <small>
                  <i class="fe fe-info me-1"></i>
                  Enter values only for positions with specific proficiency level standards; all other positions will automatically default to level 3.
                </small>
              </b-alert>
            </div>
            <div v-for="(item, index) in bodyAdd.positions" :key="index">
              <div class="col-5 mb-3">
                <label class="form-label">Position {{ index+1 }}</label>
                <v-select
                  v-model="item.position_id"
                  :options="positionOptions"
                  :loading="isLoadingPositionOptions"
                  label="position_title"
                  :reduce="(e) => e.position_id"
                  placeholder="Select Position"
                  @input="updatePositionTitle(item, $event)">
                  <template #spinner="{ loading }">
                    <div v-if="loading" class="vs__spinner">Loading...</div>
                  </template>
                </v-select>
              </div>
              <div class="col-5 mb-3">
                <label class="form-label">Proficiency Level <span class="text-danger">*</span></label>
                <input
                  type="number"
                  min="1"
                  max="5"
                  v-model="item.proficiency"
                  :disabled="bodyAdd.uniformLevel"
                  class="form-control"
                  placeholder="Enter proficiency level (1-5)" />
              </div>
              <div class="col-2 mb-3">
                <label></label>
                <b-button variant="danger" @click="removePositionProficiencyAdd(index)" class="mt-2">
                  <span class="fe fe-trash"></span>
                </b-button>
              </div>
            </div>
          </div>
          
          <!-- Proficiency Descriptions -->
          <div class="row mt-3">
            <div class="col-12">
              <h3>Proficiency Descriptions (1-5)</h3>
            </div>
            <div class="col-md-12" v-for="level in [1,2,3,4,5]" :key="level">
              <div class="form-group">
                <label>Level {{ level }} Description</label>
                <vue-editor
                  v-model="bodyAdd[`proficiency${level}_description`]"
                  :editor-toolbar="editorToolbar"
                  :placeholder="`Description for proficiency level ${level}`">
                </vue-editor>
              </div>
            </div>
          </div>
          <div class="form-group text-right">
            <button
              type="button"
              class="btn btn-secondary me-2"
              @click="$bvModal.hide('modal-competency')">
              Cancel
            </button>
            <button
              class="btn btn-danger"
              type="submit"
              :disabled="isLoading">
              <span
                v-if="isLoading"
                class="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"></span>
              Save
            </button>
          </div>
        </form>

        <!-- Edit Form -->
        <form @submit.prevent="saveUpdate()" v-else-if="isAdd === false">
          <div v-if="isLoading && !bodyUpdate.id" class="text-center py-4">
            <div class="spinner-border" role="status">
              <span class="sr-only">Loading...</span>
            </div>
            <p class="mt-2">Loading competency details...</p>
          </div>
          <div v-else>
            <div class="row">
              <div class="row col-md-6">
                <div class="col-md-12">
                  <div class="form-group">
                    <label>Competency ID <span class="text-danger">*</span></label>
                    <input
                      v-validate="'required'"
                      name="Competency ID"
                      type="text"
                      v-model="bodyUpdate.competency_id"
                      class="form-control"
                      placeholder="Competency ID" />
                    <span
                      class="text-sm text-danger"
                      v-show="errors.has('Competency ID')"
                      >{{ errors.first("Competency ID") }}</span>
                  </div>
                </div>
                <div class="col-md-12">
                  <div class="form-group">
                    <label>Name <span class="text-danger">*</span></label>
                    <input
                      v-validate="'required'"
                      name="Name"
                      type="text"
                      v-model="bodyUpdate.competency_name"
                      class="form-control"
                      placeholder="Competency Name" />
                    <span
                      class="text-sm text-danger"
                      v-show="errors.has('Name')"
                      >{{ errors.first("Name") }}</span>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="form-group">
                    <label>Object Type</label>
                    <v-select
                      v-model="bodyUpdate.object_type"
                      :options="[
                        { value: 'Q', label: 'Q' },
                        { value: 'QK', label: 'QK' }
                      ]"
                      :reduce="option => option.value"
                      label="label"
                      placeholder="Select Object Type">
                    </v-select>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="form-group">
                    <label>Parent ID</label>
                    <v-select
                      v-model="bodyUpdate.parent_id"
                      :options="competencyOptions"
                      :reduce="option => option.id"
                      :loading="isLoadingCompetencyOptions"
                      label="name"
                      placeholder="Select Parent Competency">
                      <template #spinner="{ loading }">
                        <div v-if="loading" class="vs__spinner">Loading...</div>
                      </template>
                    </v-select>
                  </div>
                </div>
              </div>
              <div class="col-md-6">
                <div class="form-group">
                  <label>Image</label>
                  <ImageCropper
                    v-model="bodyUpdate.image"
                    :preview-url="bodyUpdate.imagePreview"
                    :aspect-ratio="1.777"
                    :max-file-size="2 * 1024 * 1024"
                    @cropped="onImageCroppedUpdate"
                    @removed="onImageRemovedUpdate"
                    @error="onImageErrorUpdate"
                    @crop-status="onCropStatusChangeUpdate"
                  />
                </div>
              </div>
              <div class="col-md-12">
                <div class="form-group">
                  <label>Description</label>
                  <vue-editor
                    v-model="bodyUpdate.description"
                    :editor-toolbar="editorToolbar"
                    placeholder="Enter competency description">
                  </vue-editor>
                </div>
              </div>
            </div>
            
            <!-- Dynamic Positions & Proficiency Levels -->
            <div class="row">
              <div class="d-flex justify-content-between align-items-center">
                <h3 class="mt-2">Position & Proficiency</h3>
                <div class="d-flex align-items-center">
                  <div class="form-check me-3">
                    <input
                      class="form-check-input"
                      type="checkbox"
                      id="uniformLevelUpdate"
                      v-model="bodyUpdate.uniformLevel"
                      @change="toggleUniformLevelUpdate">
                    <label class="form-check-label" for="uniformLevelUpdate">
                      Set all positions to level 3
                    </label>
                  </div>
                  <b-button variant="primary" @click="addPositionProficiencyUpdate">
                    <span class="fe fe-file-plus"></span>
                    Add Position
                  </b-button>
                </div>
              </div>
              <div class="col-12 mt-3 mb-3">
                <b-alert variant="warning" show class="mb-0">
                  <small>
                    <i class="fe fe-info me-1"></i>
                    Enter values only for positions with specific proficiency level standards; all other positions will automatically default to level 3.
                  </small>
                </b-alert>
              </div>
              <div v-for="(item, index) in bodyUpdate.positions" :key="index">
                <div class="col-5 mb-3">
                  <label class="form-label">Position {{ index+1 }}</label>
                  <v-select
                    v-model="item.position_id"
                    :options="positionOptions"
                    :loading="isLoadingPositionOptions"
                    label="position_title"
                    :reduce="(e) => e.position_id"
                    placeholder="Select Position"
                    @input="updatePositionTitle(item, $event)">
                    <template #spinner="{ loading }">
                      <div v-if="loading" class="vs__spinner">Loading...</div>
                    </template>
                  </v-select>
                </div>
                <div class="col-5 mb-3">
                  <label class="form-label">Proficiency Level <span class="text-danger">*</span></label>
                  <input
                    type="number"
                    min="1"
                    max="5"
                    v-model="item.proficiency"
                    :disabled="bodyUpdate.uniformLevel"
                    class="form-control"
                    placeholder="Enter proficiency level (1-5)" />
                </div>
                <div class="col-2 mb-3">
                  <label></label>
                  <b-button variant="danger" @click="removePositionProficiencyUpdate(index)" class="mt-2">
                    <span class="fe fe-trash"></span>
                  </b-button>
                </div>
              </div>
            </div>
            <!-- Proficiency Descriptions -->
            <div class="row mt-3">
              <div class="col-12">
                <h3>Proficiency Descriptions (1-5)</h3>
              </div>
              <div class="col-md-12" v-for="level in [1,2,3,4,5]" :key="level">
                <div class="form-group">
                  <label>Level {{ level }} Description</label>
                  <vue-editor
                    v-model="bodyUpdate[`proficiency${level}_description`]"
                    :editor-toolbar="editorToolbar"
                    :placeholder="`Description for proficiency level ${level}`">
                  </vue-editor>
                </div>
              </div>
            </div>
            <div class="form-group text-right">
              <button
                type="button"
                class="btn btn-secondary me-2"
                @click="$bvModal.hide('modal-competency')">
                Cancel
              </button>
              <button
                class="btn btn-danger"
                type="submit"
                :disabled="isLoading">
                <span
                  v-if="isLoading"
                  class="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"></span>
                Update
              </button>
            </div>
          </div>
        </form>

        <!-- View Mode -->
        <div v-else>
          <div v-if="isLoading" class="text-center py-4">
            <div class="spinner-border" role="status">
              <span class="sr-only">Loading...</span>
            </div>
            <p class="mt-2">Loading competency details...</p>
          </div>
          <div v-else>
            <div class="row">
              <div class="col-md-6">
                <div class="form-group">
                  <label><strong>ID:</strong></label>
                  <p>{{ (competencyDetailData.id ? competencyDetailData : viewDetailData).id }}</p>
                </div>
              </div>
              <div class="col-md-6">
                <div class="form-group">
                  <label><strong>Name:</strong></label>
                  <p>{{ (competencyDetailData.id ? competencyDetailData : viewDetailData).name }}</p>
                </div>
              </div>
            </div>
            <div class="row">
              <div class="col-md-12">
                <div class="form-group">
                  <label><strong>Image:</strong></label>
                  <div 
                    class="rounded" 
                    style="min-height: 250px;">
                    <img 
                      v-if="(competencyDetailData.id ? competencyDetailData : viewDetailData).image_url || (competencyDetailData.id ? competencyDetailData : viewDetailData).thumbnail_url"
                      :src="`${imageBaseUrl}/attachments/${(competencyDetailData.id ? competencyDetailData : viewDetailData).image_url || (competencyDetailData.id ? competencyDetailData : viewDetailData).thumbnail_url}`"
                      alt="Competency Image"
                      class="img-fluid rounded"
                      style="max-width: 100%; max-height: 250px; object-fit: cover;"
                      @error="handleImageError">
                    <div v-else class="text-muted text-center">
                      <i class="fe fe-image" style="font-size: 48px; color: #ccc;"></i>
                      <p class="mt-2">No image available</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="row">
              <div class="col-md-6">
                <div class="form-group">
                  <label><strong>Object Type:</strong></label>
                  <p>{{ (competencyDetailData.id ? competencyDetailData : viewDetailData).object_type || '-' }}</p>
                </div>
              </div>
              <div class="col-md-6">
                <div class="form-group">
                  <label><strong>Parent ID:</strong></label>
                  <p>{{ (competencyDetailData.id ? competencyDetailData : viewDetailData).parent_id || '-' }}</p>
                </div>
              </div>
            </div>
            <div class="row">
              <div class="col-md-6">
                <div class="form-group">
                  <label><strong>Role Competency ID:</strong></label>
                  <p>{{ (competencyDetailData.id ? competencyDetailData : viewDetailData).role_competency ? (competencyDetailData.id ? competencyDetailData : viewDetailData).role_competency.role_competency_id : ((competencyDetailData.id ? competencyDetailData : viewDetailData).role_competency_id || '-') }}</p>
                </div>
              </div>
              <div class="col-md-6">
                <div class="form-group">
                  <label><strong>Role Competency Name:</strong></label>
                  <p>{{ (competencyDetailData.id ? competencyDetailData : viewDetailData).role_competency ? (competencyDetailData.id ? competencyDetailData : viewDetailData).role_competency.role_competency_name : '-' }}</p>
                </div>
              </div>
            </div>
            <div class="row">
              <div class="col-md-6">
                <div class="form-group">
                  <label><strong>Start Date:</strong></label>
                  <p>{{ (competencyDetailData.id ? competencyDetailData : viewDetailData).start_date ? formatDate((competencyDetailData.id ? competencyDetailData : viewDetailData).start_date) : '-' }}</p>
                </div>
              </div>
              <div class="col-md-6">
                <div class="form-group">
                  <label><strong>End Date:</strong></label>
                  <p>{{ (competencyDetailData.id ? competencyDetailData : viewDetailData).end_date ? formatDate((competencyDetailData.id ? competencyDetailData : viewDetailData).end_date) : '-' }}</p>
                </div>
              </div>
            </div>
            
            <!-- Position & Proficiency Level Table -->
            <div class="row mt-3">
              <div class="col-12">
                <h3>Position & Proficiency Levels</h3>
                <vue-good-table
                  :columns="positionProficiencyColumns"
                  :rows="formattedPositionProficiencyData"
                  :pagination-options="{
                    enabled: true
                  }"
                  :sort-options="{
                    enabled: false
                  }"
                  style-class="vgt-table table-sm border">
                  
                  <!-- Custom Rows -->
                  <template slot="table-row" slot-scope="props">
                    <!-- Number -->
                    <span v-if="props.column.field == 'number'">
                      {{ props.row.number }}
                    </span>

                    <!-- Position ID -->
                    <span v-if="props.column.field == 'position_id'">
                      {{ props.row.position_id || '-' }}
                    </span>

                    <!-- Position Title -->
                    <span v-if="props.column.field == 'position_title'">
                      {{ props.row.position_title || '-' }}
                    </span>

                    <!-- Proficiency -->
                    <span v-if="props.column.field == 'proficiency'">
                      {{ props.row.proficiency || '-' }}
                    </span>
                  </template>

                  <!-- Empty State -->
                  <div slot="emptystate" class="text-center text-muted py-3">
                    No position data available
                  </div>
                </vue-good-table>
              </div>
            </div>
            
            <!-- Proficiency Descriptions View -->
            <div class="row mt-3">
              <div class="col-12">
                <h3>Proficiency Descriptions</h3>
              </div>
              <div class="col-md-12" v-for="level in [1,2,3,4,5]" :key="level">
                <div class="form-group">
                  <label><strong>Level {{ level }}:</strong></label>
                  <div 
                    class="border p-3 bg-light rounded" 
                    v-if="(competencyDetailData.id ? competencyDetailData : viewDetailData)[`proficiency${level}_description`]"
                    v-html="(competencyDetailData.id ? competencyDetailData : viewDetailData)[`proficiency${level}_description`]">
                  </div>
                  <div 
                    class="border p-3 bg-light rounded text-muted" 
                    v-else>
                    No description available for Level {{ level }}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="form-group text-right">
            <button
              type="button"
              class="btn btn-secondary"
              @click="$bvModal.hide('modal-competency')">
              Close
            </button>
          </div>
        </div>
      </b-modal>

      <!-- Upload Modal -->
      <b-modal 
        id="modal-upload" 
        centered 
        hide-footer 
        title="Upload Competency File"
        size="lg">
        <form @submit.prevent="uploadFile()">
          <div class="row">
            <div class="col-12">
              <div class="alert alert-warning">
                <small>
                  <i class="fe fe-info me-1"></i>
                  Enter values only for positions with specific proficiency level standards; all other positions will automatically default to level 3.
                </small>
              </div>
            </div>
            <div class="col-12">
              <div class="form-group">
                <label>Select File <span class="text-danger">*</span></label>
                <input
                  ref="fileInput"
                  type="file"
                  class="form-control"
                  accept=".xlsx"
                  @change="onFileChange"
                  required />
                <small class="text-muted">Accepted format: .xlsx (max 5MB)</small>
              </div>
            </div>
            <div class="col-12" v-if="selectedFile">
              <div class="form-group">
                <label>File Preview:</label>
                <div class="card">
                  <div class="card-body">
                    <div class="d-flex align-items-center">
                      <i class="fe fe-file-text text-success me-2" style="font-size: 24px;"></i>
                      <div>
                        <strong>{{ selectedFile.name }}</strong><br>
                        <small class="text-muted">{{ formatFileSize(selectedFile.size) }}</small>
                      </div>
                      <button 
                        type="button" 
                        class="btn btn-sm btn-danger ms-auto"
                        @click="clearFile()">
                        <i class="fe fe-x"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div class="form-group text-right">
            <button
              type="button"
              class="btn btn-secondary me-2"
              @click="$bvModal.hide('modal-upload')">
              Cancel
            </button>
            <button
              class="btn btn-primary"
              type="submit"
              :disabled="!selectedFile || isUploading">
              <span
                v-if="isUploading"
                class="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"></span>
              {{ isUploading ? 'Uploading...' : 'Upload' }}
            </button>
          </div>
        </form>
      </b-modal>

    </div>
  </div>
</template>
<script>
import logic from './competency.logic.js';

export default {
  ...logic
}
</script>

<style src="./competency.css" scoped></style>
